import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Loader2, Lock, ShieldCheck } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

const AdminLogin = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [trustDevice, setTrustDevice] = useState(false);
  const [needsTwoFA, setNeedsTwoFA] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const payload = { email, password };
      if (needsTwoFA && code) payload.code = code.trim();
      if (needsTwoFA && trustDevice) payload.trust_device = true;
      // device_token is now an httpOnly cookie set by the backend on previous successful 2FA login.
      // The browser sends it automatically — no localStorage roundtrip needed.
      await axios.post(`${API}/admin/auth/login`, payload);
      // admin_token is now an httpOnly cookie. Nothing sensitive to persist here.
      toast.success("Welcome, Admin");
      navigate("/admin/dashboard");
    } catch (err) {
      const detail = err.response?.data?.detail;
      // Server signals 2FA needed via {error: "2fa_required"}
      if (detail && typeof detail === "object" && detail.error === "2fa_required") {
        setNeedsTwoFA(true);
        setError("");
        toast.info("Enter the 6-digit code from your authenticator app");
      } else {
        setError(typeof detail === "string" ? detail : "Invalid credentials");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090B] flex items-center justify-center p-6">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="w-12 h-12 bg-[#002FA7] rounded flex items-center justify-center mx-auto mb-4">
            <Lock className="w-6 h-6 text-white" />
          </div>
          <h1 className="text-xl font-bold text-white tracking-tight">
            roster<span className="text-[#002FA7]">.ai</span> Admin
          </h1>
          <p className="text-sm text-zinc-500 mt-1">Sign in to the admin panel</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-zinc-900 border border-zinc-800 p-6 rounded space-y-4">
          {error && (
            <div className="bg-red-500/10 border border-red-500/20 text-red-400 text-sm p-3 rounded">
              {error}
            </div>
          )}

          <div>
            <Label htmlFor="admin-email" className="text-xs uppercase tracking-wide text-zinc-500">
              Email
            </Label>
            <Input
              id="admin-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="mt-1 bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-600"
              placeholder="admin@roster.ai"
              data-testid="admin-email-input"
            />
          </div>

          <div>
            <Label htmlFor="admin-password" className="text-xs uppercase tracking-wide text-zinc-500">
              Password
            </Label>
            <Input
              id="admin-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="mt-1 bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-600"
              placeholder="Enter password"
              data-testid="admin-password-input"
            />
          </div>

          {needsTwoFA && (
            <div data-testid="admin-2fa-block">
              <Label htmlFor="admin-2fa" className="text-xs uppercase tracking-wide text-zinc-500 flex items-center gap-1.5">
                <ShieldCheck className="w-3 h-3" /> 2FA code or recovery code
              </Label>
              <Input
                id="admin-2fa"
                type="text"
                inputMode="text"
                autoComplete="one-time-code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                required
                autoFocus
                className="mt-1 bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-600 font-mono tracking-widest"
                placeholder="123 456"
                data-testid="admin-2fa-input"
              />
              <p className="text-[11px] text-zinc-500 mt-1">
                Open your authenticator app and enter the 6-digit code, or use one of your 8-character recovery codes.
              </p>
              <label className="mt-3 flex items-center gap-2 text-xs text-zinc-300 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={trustDevice}
                  onChange={(e) => setTrustDevice(e.target.checked)}
                  className="accent-[#002FA7] w-4 h-4"
                  data-testid="admin-trust-device-checkbox"
                />
                Trust this device for 30 days (skip 2FA next time)
              </label>
            </div>
          )}

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-[#002FA7] hover:bg-[#00227A] text-white"
            data-testid="admin-login-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (needsTwoFA ? "Verify code" : "Sign In")}
          </Button>
        </form>

        <p className="text-center text-xs text-zinc-600 mt-6">
          <a href="/" className="hover:text-zinc-400 transition-colors">Back to roster.ai</a>
        </p>
      </div>
    </div>
  );
};

export default AdminLogin;
